import { useTranslation } from 'react-i18next';
import { useState, createContext, useContext } from 'react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu as MenuIcon } from 'lucide-react';
import { isPathActive } from '@/lib/navigationItems';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from "@/lib/utils"

// Context to provide closeMenu function to children
const MenuContext = createContext<{ closeMenu: () => void } | null>(null);

export const useMenuContext = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenuContext must be used within MenuWrapper');
  }
  return context;
};

interface MenuWrapperProps {
  children: React.ReactNode;
  needsNotificationSetup?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Paths that should make the "More" button appear active when matched */
  menuPaths?: string[];
  /** Grey button row at top (e.g. settings + live support). */
  utilityItems?: React.ReactNode;
}

export function MenuWrapper({
  children,
  utilityItems,
  isOpen: externalIsOpen,
  onOpenChange: externalOnOpenChange,
  menuPaths = [],
}: MenuWrapperProps) {
  const { t } = useTranslation();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  
  // Check if current path matches any menu item path (makes "More" button active)
  const isMoreActive = menuPaths.length > 0 && isPathActive(location.pathname, menuPaths);
  
 

  const closeMenu = () => {
    if (externalOnOpenChange) {
      externalOnOpenChange(false);
    } else {
      setIsOpen(false);
    }
  };

  return (
    <>
      <Sheet 
        open={externalIsOpen !== undefined ? externalIsOpen : isOpen} 
        onOpenChange={externalOnOpenChange || setIsOpen}>
        <SheetTrigger asChild>
          <Button 
            variant="ghost" 
            className={`bottom-nav-button !px-0 !py-0 !flex !flex-col !items-center !justify-center gap-1 hover:bg-transparent hover:!text-current ${isMoreActive ? 'text-primary' : ''}`}
          >
            <div className="relative flex items-center justify-center">
              <MenuIcon className="size-6" />
            </div>
            <span className="text-bottom-nav">{t('navigation.more', 'More')}</span>
          </Button>
        </SheetTrigger>
        <SheetContent 
          side="top" 
          hideCloseButton
          className={cn(
            'max-h-[80vh] flex flex-col px-6 pb-6',
            /* Same native top inset as sticky-header-notch-backdrop (Android env(top)≈0); md handled in CSS */
            'pt-safe-native-top',
          )}>
          <MenuContext.Provider value={{ closeMenu }}>
            {utilityItems ? (
              <div className="pb-3">
                <div className="flex w-full max-w-xs mx-auto gap-2">{utilityItems}</div>
              </div>
            ) : null}

            {/* Primary menu */}
            <div className="flex flex-col py-4">
              <div className="space-y-1 max-w-xs mx-auto w-full">
                {children}
              </div>
            </div>
          </MenuContext.Provider>
        </SheetContent>
      </Sheet>
    </>
  );
}
